import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  BarChart3,
  BookMarked,
  BookOpen,
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Globe,
  GraduationCap,
  Images,
  Landmark,
  Layers,
  LayoutDashboard,
  LogOut,
  Newspaper,
  Shirt,
  Timer,
  UserCheck,
  UserCog,
  Users,
  Vote,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const ALL = ['admin', 'editor', 'viewer'];
const EDIT = ['admin', 'editor'];
const ADMIN = ['admin'];

/**
 * Sidebar navigation grouped into labelled sections separated by dividers.
 * Each entry keeps the original role restrictions.
 */
const NAV_SECTIONS = [
  {
    label: 'Overview',
    items: [{ path: '/admin', icon: LayoutDashboard, label: 'Dashboard', roles: ALL, end: true }],
  },
  {
    label: 'Academics',
    items: [
      { path: '/admin/graduation-days', icon: CalendarDays, label: 'Graduation Days', roles: EDIT },
      { path: '/admin/departments', icon: Landmark, label: 'Departments', roles: ADMIN },
      {
        label: 'Courses',
        icon: BookOpen,
        roles: EDIT,
        isDropdown: true,
        children: [
          { path: '/admin/courses', icon: BookOpen, label: 'All Courses', roles: EDIT },
          { path: '/admin/course-levels', icon: Layers, label: 'Course Levels', roles: ADMIN },
          { path: '/admin/exam-bodies', icon: GraduationCap, label: 'Exam Bodies', roles: ADMIN },
        ],
      },
    ],
  },
  {
    label: 'Graduation',
    items: [
      { path: '/admin/graduates', icon: GraduationCap, label: 'Graduates', roles: ALL },
      { path: '/admin/gowns', icon: Shirt, label: 'Gown Requests', roles: EDIT },
      { path: '/admin/booklets', icon: BookMarked, label: 'Booklets', roles: EDIT },
      { path: '/admin/countdown', icon: Timer, label: 'Countdown', roles: EDIT },
    ],
  },
  {
    label: 'Community',
    items: [
      { path: '/admin/alumni', icon: Users, label: 'Alumni Committee', roles: EDIT },
      { path: '/admin/registered-alumni', icon: UserCheck, label: 'Registered Alumni', roles: EDIT },
      { path: '/admin/nominations', icon: Vote, label: 'Nominations', roles: EDIT },
      { path: '/admin/blog', icon: Newspaper, label: 'Blog & News', roles: EDIT },
      { path: '/admin/gallery', icon: Images, label: 'Gallery', roles: EDIT },
    ],
  },
  {
    label: 'System',
    items: [
      { path: '/admin/reports', icon: BarChart3, label: 'Reports', roles: ALL },
      { path: '/admin/users', icon: UserCog, label: 'Users', roles: ADMIN },
    ],
  },
];

const Sidebar = ({ isCollapsed, toggleSidebar }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isCoursesOpen, setIsCoursesOpen] = useState(false);

  const role = user?.role || 'viewer';
  const canSee = (roles) => roles.includes(role);

  const sections = NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) =>
      item.isDropdown ? item.children.some((child) => canSee(child.roles)) : canSee(item.roles)
    ),
  })).filter((section) => section.items.length > 0);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const linkBase =
    'group relative flex items-center rounded-lg text-sm font-medium transition-colors focus-visible:outline-none';

  return (
    <>
      {/* Desktop sidebar */}
      <aside
        className={`hidden shrink-0 flex-col border-r border-white/10 bg-brand-800 text-white transition-[width] duration-300 md:flex ${
          isCollapsed ? 'w-[76px]' : 'w-64'
        }`}
      >
        {/* Brand + collapse */}
        <div className="flex h-16 items-center gap-3 border-b border-white/10 px-4">
          <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg bg-white/10 p-1 ring-1 ring-white/15">
            <img
              src="/images/logo.png"
              alt=""
              className="h-full w-full object-contain"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.parentElement.textContent = 'K';
                e.currentTarget.parentElement.className +=
                  ' text-gold-400 font-extrabold text-base justify-center items-center flex';
              }}
            />
          </span>
          {!isCollapsed && (
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-bold leading-tight text-white">Katine TTI</p>
              <p className="truncate text-2xs uppercase tracking-wider text-gold-300">
                Admin Console
              </p>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="sidebar-scroll flex-1 overflow-y-auto px-2 py-3" aria-label="Admin">
          {sections.map((section, sectionIndex) => (
            <div key={section.label}>
              {sectionIndex > 0 && (
                <hr
                  className={`my-2 border-white/10 ${isCollapsed ? 'mx-2' : ''}`}
                  aria-hidden="true"
                />
              )}

              {!isCollapsed && (
                <p className="px-3 pb-1.5 pt-1 text-2xs font-bold uppercase tracking-widest text-white/40">
                  {section.label}
                </p>
              )}

              <ul className="space-y-0.5">
                {section.items.map((item) =>
                  item.isDropdown ? (
                    <li key={item.label}>
                      <button
                        type="button"
                        onClick={() => setIsCoursesOpen((open) => !open)}
                        aria-expanded={isCoursesOpen}
                        title={isCollapsed ? item.label : undefined}
                        className={`${linkBase} w-full py-2 text-white/80 hover:bg-white/10 hover:text-white ${
                          isCollapsed ? 'justify-center' : 'justify-between px-3'
                        }`}
                      >
                        <span className="flex items-center gap-2.5">
                          <item.icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                          {!isCollapsed && item.label}
                        </span>
                        {!isCollapsed && (
                          <ChevronDown
                            className={`h-4 w-4 transition-transform ${
                              isCoursesOpen ? 'rotate-180' : ''
                            }`}
                            aria-hidden="true"
                          />
                        )}
                      </button>

                      {!isCollapsed && isCoursesOpen && (
                        <ul className="mb-1 mt-0.5 space-y-0.5 border-l border-white/15 pl-3">
                          {item.children
                            .filter((child) => canSee(child.roles))
                            .map((child) => (
                              <li key={child.path}>
                                <NavLink
                                  to={child.path}
                                  className={({ isActive }) =>
                                    `${linkBase} gap-2.5 px-3 py-1.5 ${
                                      isActive
                                        ? 'bg-white/15 text-white'
                                        : 'text-white/70 hover:bg-white/10 hover:text-white'
                                    }`
                                  }
                                >
                                  <child.icon
                                    className="h-3.5 w-3.5 flex-shrink-0"
                                    aria-hidden="true"
                                  />
                                  <span className="truncate">{child.label}</span>
                                </NavLink>
                              </li>
                            ))}
                        </ul>
                      )}
                    </li>
                  ) : (
                    <li key={item.path}>
                      <NavLink
                        to={item.path}
                        end={item.end}
                        title={isCollapsed ? item.label : undefined}
                        className={({ isActive }) =>
                          `${linkBase} gap-2.5 py-2 ${
                            isCollapsed ? 'justify-center' : 'px-3'
                          } ${
                            isActive
                              ? 'bg-white/15 text-white'
                              : 'text-white/80 hover:bg-white/10 hover:text-white'
                          }`
                        }
                      >
                        {({ isActive }) => (
                          <>
                            <span
                              className={`absolute left-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-r bg-gold-400 transition-opacity ${
                                isActive ? 'opacity-100' : 'opacity-0'
                              }`}
                              aria-hidden="true"
                            />
                            <item.icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                            {!isCollapsed && <span className="truncate">{item.label}</span>}
                          </>
                        )}
                      </NavLink>
                    </li>
                  )
                )}
              </ul>
            </div>
          ))}
        </nav>

        {/* Footer actions */}
        <div className="border-t border-white/10 p-2">
          <div className="mb-2 flex items-center gap-2 rounded-lg bg-white/5 px-2 py-2">
            <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gold-400 text-xs font-bold text-brand-950">
              {(user?.name || 'A').charAt(0).toUpperCase()}
            </span>
            {!isCollapsed && (
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-semibold text-white">
                  {user?.name || 'Administrator'}
                </p>
                <p className="truncate text-2xs uppercase tracking-wide text-gold-300">
                  {role}
                </p>
              </div>
            )}
          </div>

          <NavLink
            to="/"
            title="View public site"
            className={`${linkBase} gap-2.5 py-2 text-white/70 hover:bg-white/10 hover:text-white ${
              isCollapsed ? 'justify-center' : 'px-3'
            }`}
          >
            <Globe className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
            {!isCollapsed && 'Public site'}
          </NavLink>

          <button
            type="button"
            onClick={handleLogout}
            title="Logout"
            className={`${linkBase} gap-2.5 py-2 text-white/70 hover:bg-red-600 hover:text-white ${
              isCollapsed ? 'justify-center' : 'px-3'
            }`}
          >
            <LogOut className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
            {!isCollapsed && 'Logout'}
          </button>

          <button
            type="button"
            onClick={toggleSidebar}
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className={`${linkBase} mt-1 w-full gap-2.5 py-2 text-white/50 hover:bg-white/10 hover:text-white ${
              isCollapsed ? 'justify-center' : 'px-3'
            }`}
          >
            {isCollapsed ? (
              <ChevronRight className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
            ) : (
              <>
                <ChevronLeft className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                <span>Collapse</span>
              </>
            )}
          </button>
        </div>
      </aside>

      {/* Mobile bottom navigation */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-40 border-t border-white/10 bg-brand-800 md:hidden"
        aria-label="Admin shortcuts"
      >
        <div className="flex items-stretch justify-around">
          {[
            { to: '/admin', icon: LayoutDashboard, label: 'Home', end: true },
            { to: '/admin/graduates', icon: GraduationCap, label: 'Graduates' },
            { to: '/admin/gowns', icon: Shirt, label: 'Gowns' },
            { to: '/admin/alumni', icon: Users, label: 'Alumni' },
          ].map(({ to, icon: Icon, label, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                `flex flex-1 flex-col items-center gap-1 py-2.5 text-2xs font-medium transition-colors ${
                  isActive ? 'text-gold-400' : 'text-white/70'
                }`
              }
            >
              <Icon className="h-5 w-5" aria-hidden="true" />
              {label}
            </NavLink>
          ))}
          <button
            type="button"
            onClick={handleLogout}
            className="flex flex-1 flex-col items-center gap-1 py-2.5 text-2xs font-medium text-white/70"
          >
            <LogOut className="h-5 w-5" aria-hidden="true" />
            Exit
          </button>
        </div>
      </nav>

      {/* Spacer so the mobile nav never covers content */}
      <div className="md:hidden" aria-hidden="true" />
    </>
  );
};

export default Sidebar;
